import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, MenuItem, Chip, CircularProgress,
  Alert, Card, CardContent, Tabs, Tab, TablePagination
} from '@mui/material';
import { PictureAsPdf, Person, Groups } from '@mui/icons-material';
import { salaryService, userService } from '../services/api';
import { generatePayslipPDF } from './SalaryReport';
import { usePagination } from '../hooks/usePagination';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function fmt(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount);
}

const AdminPayslips: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [payslips, setPayslips] = useState<any[]>([]);
  const [allPayslips, setAllPayslips] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Deactivated employees don't show up in the regular Employee lookup here.
    userService.getUsers().then((data: any[]) => setUsers((data || []).filter(u => u.isActive))).catch(() => {});
  }, []);

  // Fetch single-employee payslips
  useEffect(() => {
    if (tab !== 0 || selectedUserId === '') return;
    setLoading(true);
    setError('');
    salaryService.getPaymentHistory(selectedUserId as number, undefined, year)
      .then(setPayslips)
      .catch(() => setError('Failed to load payslips.'))
      .finally(() => setLoading(false));
  }, [selectedUserId, year, tab]);

  // Fetch all-employees summary
  useEffect(() => {
    if (tab !== 1) return;
    setLoading(true);
    setError('');
    salaryService.getPaymentHistory(undefined, undefined, year)
      .then(setAllPayslips)
      .catch(() => setError('Failed to load payslip data.'))
      .finally(() => setLoading(false));
  }, [year, tab]);

  const selectedUser = users.find(u => u.id === selectedUserId);

  // Per-employee YTD totals for summary tab
  const empSummary = React.useMemo(() => {
    const map = new Map<number, any>();
    allPayslips.forEach(p => {
      if (!map.has(p.userId)) {
        map.set(p.userId, {
          userId: p.userId,
          userName: p.userName,
          designation: p.designation,
          department: p.department,
          employeeCode: p.employeeCode,
          monthCount: 0,
          ytdGross: 0,
          ytdDeductions: 0,
          ytdNet: 0,
          monthlySalary: p.monthlySalary,
        });
      }
      const e = map.get(p.userId)!;
      e.monthCount++;
      e.ytdGross += p.monthlySalary;
      e.ytdDeductions += p.lwpDeduction;
      e.ytdNet += p.netSalary;
    });
    return Array.from(map.values());
  }, [allPayslips]);

  const { pagedRows: pagedPayslips, paginationProps: payslipsPaginationProps } = usePagination(payslips);
  const { pagedRows: pagedEmpSummary, paginationProps: empSummaryPaginationProps } = usePagination(empSummary);

  const handleDownload = (p: any) => {
    const monthLabel = `${MONTHS[p.month - 1]} ${p.year}`;
    generatePayslipPDF(p, monthLabel);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Employee Payslips</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Person fontSize="small" />} iconPosition="start" label="Employee View" />
        <Tab icon={<Groups fontSize="small" />} iconPosition="start" label="All Employees Summary" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Tab 0: Employee View ─────────────────────────────────── */}
      {tab === 0 && (
        <>
          {/* Filters */}
          <Paper elevation={2} sx={{ p: 2.5, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select label="Employee" value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value))}
              size="small" sx={{ minWidth: 240 }}
            >
              <MenuItem value="">— Select Employee —</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>)}
            </TextField>
            <TextField
              label="Year" type="number" value={year}
              onChange={e => setYear(Number(e.target.value))}
              size="small" sx={{ width: 110 }}
            />
          </Paper>

          {/* Employee card */}
          {selectedUser && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography fontWeight="bold">{selectedUser.fullName}</Typography>
                </Box>
                {payslips[0]?.designation && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Designation</Typography>
                    <Typography>{payslips[0].designation}</Typography>
                  </Box>
                )}
                {payslips[0]?.department && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Department</Typography>
                    <Typography>{payslips[0].department}</Typography>
                  </Box>
                )}
                {payslips[0]?.employeeCode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Employee Code</Typography>
                    <Typography>{payslips[0].employeeCode}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Monthly Salary</Typography>
                  <Typography fontWeight="bold" color="success.main">
                    {payslips[0] ? fmt(payslips[0].monthlySalary) : fmt(selectedUser.salary ?? 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Annual CTC</Typography>
                  <Typography fontWeight="bold">
                    {fmt((selectedUser.salary ?? 0) * 12)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Payslips table */}
          {selectedUserId === '' ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Select an employee to view their payslips.
            </Paper>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : payslips.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No payslips found for {year}.
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Month', 'Gross', 'Working Days', 'Present', 'Paid Leave', 'Absent', 'LWP', 'Deduction', 'Net Salary', 'Status', ''].map(h => (
                      <TableCell key={h} align={h === 'Month' || h === '' ? 'left' : 'right'}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedPayslips.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>{MONTHS[p.month - 1]} {p.year}</TableCell>
                      <TableCell align="right">{fmt(p.monthlySalary)}</TableCell>
                      <TableCell align="right">{p.workingDays}</TableCell>
                      <TableCell align="right">{p.presentDays}</TableCell>
                      <TableCell align="right" sx={{ color: p.paidLeaveDays > 0 ? 'info.main' : 'inherit' }}>{p.paidLeaveDays}</TableCell>
                      <TableCell align="right" sx={{ color: p.absentDays > 0 ? 'error.main' : 'inherit' }}>{p.absentDays}</TableCell>
                      <TableCell align="right" sx={{ color: p.lwpDays > 0 ? 'error.main' : 'inherit' }}>{p.lwpDays}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(p.lwpDeduction)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{fmt(p.netSalary)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={p.paymentStatus}
                          size="small"
                          color={p.paymentStatus === 'Paid' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small" variant="outlined"
                          startIcon={<PictureAsPdf fontSize="small" />}
                          onClick={() => handleDownload(p)}
                        >
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {payslips.length > 0 && <TablePagination {...payslipsPaginationProps} />}
            </TableContainer>
          )}
        </>
      )}

      {/* ── Tab 1: All Employees Summary ─────────────────────────── */}
      {tab === 1 && (
        <>
          <Paper elevation={2} sx={{ p: 2.5, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Year" type="number" value={year}
              onChange={e => setYear(Number(e.target.value))}
              size="small" sx={{ width: 110 }}
            />
            <Typography variant="body2" color="text.secondary">
              Showing YTD totals for all employees in {year}.
            </Typography>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : empSummary.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No payslip data found for {year}.
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Employee', 'Code', 'Designation', 'Months Processed', 'YTD Gross', 'YTD Deductions', 'YTD Net'].map(h => (
                      <TableCell key={h} align={h === 'Employee' || h === 'Code' || h === 'Designation' ? 'left' : 'right'}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedEmpSummary.map(e => (
                    <TableRow key={e.userId} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>{e.userName}</TableCell>
                      <TableCell>{e.employeeCode || '—'}</TableCell>
                      <TableCell>{e.designation || '—'}</TableCell>
                      <TableCell align="right">{e.monthCount}</TableCell>
                      <TableCell align="right">{fmt(e.ytdGross)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(e.ytdDeductions)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{fmt(e.ytdNet)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {empSummary.length > 0 && <TablePagination {...empSummaryPaginationProps} />}
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default AdminPayslips;
